import React from 'react';
import {
  Leaf,
  ShoppingBag,
  Menu,
  ArrowLeft,
  Shield,
  Plus,
  ChevronDown,
  Download,
  Smartphone,
  RotateCw,
} from 'lucide-react';
import type { Estate, Page } from '../../types';
import type { CustomerWorkspace } from '../../lib/customer-workspace';

export interface MobileTopBarProps {
  estate?: Estate;
  estates: Estate[];
  selectedEstateId: string;
  onSelectEstate: (estateId: string) => void;
  customerWorkspace: CustomerWorkspace | null;
  onLeaveCustomer?: () => void;
  isAdmin?: boolean;
  cartCount: number;
  onOpenCart: () => void;
  onOpenMenu: () => void;
  menuOpen: boolean;
  page: Page;
  onNavigate: (page: Page) => void;
  allowEstateCreation?: boolean;
  onCreateEstate?: () => void;
  onOpenExportSheet?: () => void;
  onOpenPwaInstall?: () => void;
  hasAppUpdate?: boolean;
  onApplyAppUpdate?: () => void;
  lastUpdatedLabel?: string;
  isGuest?: boolean;
  fieldCount?: number;
  onOpenSavePlan?: () => void;
}

function getPageTitle(page: Page): string {
  switch (page) {
    case 'plans':
      return 'Plejeplaner';
    case 'overview':
      return 'Revir Overblik';
    case 'profile':
      return 'Profil & Licens';
    case 'raccoon-map':
      return 'Mårhundekort';
    case 'catalog':
      return 'Frøkatalog';
    case 'integrations':
      return 'Jagt & Sensorer';
    case 'forest':
      return 'Skovkort';
    case 'tasks':
      return 'Opgaver';
    case 'activities':
      return 'Aktiviteter';
    case 'documents':
      return 'Dokumenter';
    case 'seeding':
      return 'Såningsgennemgang';
    case 'biotope-plan':
      return 'Biotopplan';
    case 'advisor':
      return 'Rådgivning';
    case 'clients':
      return 'Mine kunder';
    case 'admin-overview':
      return 'Cockpit & Nøgletal';
    case 'admin-actions':
      return 'Handlingscenter';
    case 'superadmin':
      return 'Kunder & Revirer';
    case 'admin-estates':
      return 'Revirfortegnelse';
    case 'admin-seeds':
      return 'Frø- & Sæsonbehov';
    case 'admin-map':
      return 'Danmark Revirkort';
    case 'admin-email':
      return 'E-mailliste';
    case 'admin-activity':
      return 'Aktivitetsfeed';
    case 'admin-stats':
    case 'admin-traffic':
      return 'Besøgsstatistik';
    default:
      return 'Plotwise';
  }
}

export const MobileTopBar: React.FC<MobileTopBarProps> = ({
  estate,
  estates,
  selectedEstateId,
  onSelectEstate,
  customerWorkspace,
  onLeaveCustomer,
  isAdmin,
  cartCount,
  onOpenCart,
  onOpenMenu,
  menuOpen,
  page,
  onNavigate,
  allowEstateCreation,
  onCreateEstate,
  onOpenExportSheet,
  onOpenPwaInstall,
  hasAppUpdate,
  onApplyAppUpdate,
  lastUpdatedLabel,
  isGuest,
  fieldCount = 0,
  onOpenSavePlan,
}) => {
  // 1. Customer Workspace View (Advisor / SuperAdmin in client revir)
  if (customerWorkspace) {
    return (
      <header className="mobile-topbar" aria-label="Mobil topbar">
        <div className="customer-workspace-banner mobile-topbar-left mobile-customer-left">
          <button
            type="button"
            className="mobile-topbar-btn mobile-leave-customer-btn"
            onClick={onLeaveCustomer}
            aria-label="Forlad kundens arbejdsplads"
            title="Forlad kundens arbejdsplads"
          >
            <ArrowLeft size={16} />
            <span>Tilbage</span>
          </button>

          <div className="mobile-customer-info-pill">
            <span className="mobile-customer-pro-tag">PRO</span>
            <span className="mobile-customer-name-text" title={customerWorkspace.name}>
              {customerWorkspace.name}
            </span>
          </div>
        </div>

        <div className="mobile-topbar-actions">
          {onOpenExportSheet && (
            <button
              type="button"
              className="mobile-topbar-btn mobile-export-trigger-btn"
              onClick={onOpenExportSheet}
              aria-label="Eksportér kortdata"
              title="Eksportér kortdata (DXF/CSV)"
            >
              <Download size={17} />
            </button>
          )}

          <button
            type="button"
            className="mobile-topbar-btn mobile-cart-btn"
            onClick={onOpenCart}
            aria-label="Åbn indkøbsliste"
            title="Indkøbsliste"
          >
            <ShoppingBag size={18} />
            {cartCount > 0 && (
              <span className="mobile-cart-badge" data-count={cartCount} aria-hidden="true" />
            )}
          </button>
        </div>
      </header>
    );
  }

  // 2. Subpage Navigation View (e.g. Plejeplaner, Overblik, Profil)
  if (page !== 'map') {
    return (
      <header className="mobile-topbar" aria-label="Mobil topbar">
        <div className="mobile-topbar-left">
          <button
            type="button"
            className="mobile-topbar-btn mobile-subpage-back-btn"
            onClick={() => onNavigate('map')}
            aria-label="Kort, tilbage til kortet"
            title="Tilbage til kortet"
          >
            <ArrowLeft size={18} />
            <span style={{ fontSize: '0.84rem', fontWeight: 600 }}>Kort</span>
          </button>
          <div className="mobile-subpage-header-title">
            <span className="mobile-subpage-title-text" role="heading" aria-level={2}>
              {getPageTitle(page)}
            </span>
          </div>
        </div>

        <div className="mobile-topbar-actions">
          {isGuest && fieldCount > 0 && onOpenSavePlan && (
            <button
              type="button"
              className="mobile-topbar-btn mobile-save-plan-btn"
              onClick={onOpenSavePlan}
              aria-label="Gem min plan"
              title="Gem min plan"
              style={{
                background: '#16a34a',
                color: '#ffffff',
                padding: '0 12px',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                minHeight: '44px',
                minWidth: '44px',
              }}
            >
              Gem plan
            </button>
          )}
          <button
            type="button"
            className="mobile-topbar-btn mobile-cart-btn"
            onClick={onOpenCart}
            aria-label="Åbn indkøbsliste"
            title="Indkøbsliste"
          >
            <ShoppingBag size={18} />
            {cartCount > 0 && (
              <span className="mobile-cart-badge" data-count={cartCount} aria-hidden="true" />
            )}
          </button>

          <button
            type="button"
            className="mobile-topbar-btn mobile-menu-btn mobile-menu icon-button"
            onClick={onOpenMenu}
            aria-label="Åbn menu"
            aria-expanded={menuOpen}
            title="Åbn menu"
          >
            <Menu size={20} />
          </button>
        </div>
      </header>
    );
  }

  // 3. Main Map Header (Standard / Own Revir)
  return (
    <header className="mobile-topbar" aria-label="Mobil topbar">
      <div className="mobile-topbar-left">
        <button
          type="button"
          className="mobile-topbar-btn mobile-menu-btn"
          onClick={onOpenMenu}
          aria-label="Åbn menu"
          aria-expanded={menuOpen}
          aria-controls="main-navigation"
          title="Menu"
        >
          <Menu size={20} />
        </button>

        <button
          type="button"
          className="mobile-brand-btn"
          onClick={() => onNavigate(isAdmin ? 'superadmin' : 'overview')}
          aria-label="Plotwise logo"
        >
          <span className="mobile-brand-mark">
            <Leaf size={16} />
          </span>
          <span className="mobile-brand-text">
            plotwise<span className="mobile-brand-dot">.</span>
          </span>
        </button>
      </div>

      <div className="mobile-topbar-center">
        <div className="mobile-estate-selector-wrap">
          {isGuest ? (
            <span className="mobile-estate-fallback-name">{estate?.name || 'Mit Revir'}</span>
          ) : estates.length > 0 ? (
            <div className="mobile-estate-select-box">
              <select
                className="mobile-estate-select"
                value={selectedEstateId || estates[0]?.id || ''}
                onChange={(e) => {
                  if (e.target.value === '__new__') {
                    onCreateEstate?.();
                  } else {
                    onSelectEstate(e.target.value);
                  }
                }}
                aria-label="Vælg revir"
              >
                {estates.map((e) => (
                  <option
                    key={e.id}
                    value={e.id}
                    style={{ background: '#102116', color: '#f1f5f9' }}
                  >
                    {e.name}
                  </option>
                ))}
                {allowEstateCreation && (
                  <option value="__new__" style={{ background: '#102116', color: '#4ade80' }}>
                    + Nyt revir…
                  </option>
                )}
              </select>
              <ChevronDown size={14} className="mobile-select-arrow" aria-hidden="true" />
            </div>
          ) : (
            <span className="mobile-estate-fallback-name">{estate?.name || 'Intet revir'}</span>
          )}
        </div>
      </div>

      <div className="mobile-topbar-actions">
        {isGuest && fieldCount > 0 && onOpenSavePlan && (
          <button
            type="button"
            className="mobile-topbar-btn mobile-save-plan-btn"
            onClick={onOpenSavePlan}
            aria-label="Gem min plan"
            title="Gem min plan"
            style={{
              background: '#16a34a',
              color: '#ffffff',
              padding: '0 12px',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              minHeight: '44px',
              minWidth: '44px',
            }}
          >
            Gem plan
          </button>
        )}
        {onApplyAppUpdate && (
          <button
            type="button"
            className="mobile-topbar-btn mobile-update-btn"
            onClick={onApplyAppUpdate}
            title={lastUpdatedLabel || 'Opdater Plotwise app'}
            aria-label="Opdater Plotwise app"
            style={{ position: 'relative' }}
          >
            <RotateCw size={17} color={hasAppUpdate ? '#4ade80' : '#e2e8f0'} />
            {hasAppUpdate && (
              <span
                style={{
                  position: 'absolute',
                  top: 7,
                  right: 7,
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: '#22c55e',
                  boxShadow: '0 0 6px #22c55e',
                }}
              />
            )}
          </button>
        )}
        <button
          type="button"
          className="mobile-topbar-btn mobile-cart-btn"
          onClick={onOpenCart}
          aria-label="Åbn indkøbsliste"
          title="Indkøbsliste"
        >
          <ShoppingBag size={18} />
          {cartCount > 0 && (
            <span className="mobile-cart-badge" data-count={cartCount} aria-hidden="true" />
          )}
        </button>
      </div>
    </header>
  );
};
